import { getAdaptedPriceTab } from '~/utils/adapters'
import { format, parseISO } from 'date-fns'
import type { TRecord } from '~/src/types/common'
import { getDataByHashes, parseFilters } from '~/utils/chart-tab'
import { useChartSidebarFilterStore } from '~/stores/chartSidebarFilter'

type TGetData = {
  chartOptions: TRecord
  startDate: string
  endDate: string
  currency?: string
  category: string
  hashes: string[]
  authorized: boolean
}

export const useChartTab = () => {
  const { $browser, $restClient } = useNuxtApp()
  const chartSidebarFilterStore = useChartSidebarFilterStore()

  const hasCatalogMineral = (mineral) =>
    Object.hasOwn(chartSidebarFilterStore.catalog, mineral)

  const getUnAuthData = async ({ category, filters }) => {
    const response = await $restClient.getMarketPortal(category, filters)

    const data: TRecord = getAdaptedPriceTab(response)

    return {
      response: data,
      showOverlay: data.y1.hideChartComponent,
      dataForDownload: null,
    }
  }

  const getAuthData = async ({ category, filters }) => {
    const response = await $restClient.getPricesByProduct(category, filters)

    return {
      response,
      showOverlay: false,
      dataForDownload: response,
    }
  }

  const getData = async (params: TGetData) => {
    const request = params.authorized ? getAuthData : getUnAuthData
    const startDateFormatted = format(parseISO(params.startDate), 'yyyy-MM-dd')
    const endDateFormatted = format(parseISO(params.endDate), 'yyyy-MM-dd')
    const { currency } = params

    const parsedFilters = parseFilters({
      category: params.category,
      chartOptions: params.chartOptions,
    })

    const filters = $browser.isMobile()
      ? {
          ...parsedFilters,
          from: startDateFormatted,
          to: endDateFormatted,
          currency,
        }
      : {
          from: startDateFormatted,
          to: endDateFormatted,
          currency,
        }

    const response = await request({
      category: params.category,
      filters,
    })

    return {
      data: getDataByHashes(response?.response, params.hashes),
      showOverlay: response?.showOverlay,
      dataForDownload: response?.dataForDownload,
    }
  }

  const fetchCatalog = async (mineral, authorized) => {
    if (!authorized) return

    if (hasCatalogMineral(mineral)) return

    const response = await $restClient.getPricesCatalogByMineral(mineral)
    chartSidebarFilterStore.setCatalogByMineral({
      mineral: mineral,
      response,
    })
  }

  return { getData, fetchCatalog }
}
